import { readTiandituPublicToken } from './runtimeConfig'

const TIANDITU_SCRIPT_ID = 'tianditu-api'
const TIANDITU_API_BASE_URL = 'https://api.tianditu.gov.cn/api?v=4.0'

type TiandituWindow = Window & { T?: unknown }

let tiandituLoadPromise: Promise<void> | null = null

const hasTiandituNamespace = (): boolean =>
    typeof window !== 'undefined' && Boolean((window as TiandituWindow).T)

const getTiandituScriptSrc = (): string => {
    const tiandituToken = readTiandituPublicToken()

    if (!tiandituToken) {
        throw new Error(
            'TIANDITU_BROWSER_TOKEN or VITE_TIANDITU_TK is not configured'
        )
    }

    return `${TIANDITU_API_BASE_URL}&tk=${encodeURIComponent(tiandituToken)}`
}

export const loadTiandituApi = async (): Promise<void> => {
    if (hasTiandituNamespace()) {
        return
    }

    if (tiandituLoadPromise) {
        return tiandituLoadPromise
    }

    tiandituLoadPromise = new Promise<void>((resolve, reject) => {
        const scriptSrc = getTiandituScriptSrc()
        const existingScript = document.getElementById(
            TIANDITU_SCRIPT_ID
        ) as HTMLScriptElement | null

        if (hasTiandituNamespace()) {
            resolve()
            return
        }

        const script = existingScript ?? document.createElement('script')

        const cleanup = () => {
            script.removeEventListener('load', handleLoad)
            script.removeEventListener('error', handleError)
        }

        const handleLoad = () => {
            cleanup()

            if (hasTiandituNamespace()) {
                script.dataset.loaded = 'true'
                resolve()
                return
            }

            tiandituLoadPromise = null
            reject(new Error('Tianditu API loaded but window.T is unavailable'))
        }

        const handleError = () => {
            cleanup()
            tiandituLoadPromise = null
            reject(new Error('Failed to load Tianditu API'))
        }

        if (existingScript) {
            if (
                existingScript.dataset.loaded === 'true' ||
                hasTiandituNamespace()
            ) {
                resolve()
                return
            }
        } else {
            script.id = TIANDITU_SCRIPT_ID
            script.src = scriptSrc
            script.async = true
        }

        script.addEventListener('load', handleLoad, { once: true })
        script.addEventListener('error', handleError, { once: true })

        if (!existingScript) {
            document.head.appendChild(script)
        }
    })

    return tiandituLoadPromise
}
